/**
 * NiV tool — WhatsApp (Meta Cloud API) control panel.
 * Shows config/number status per channel ("users" / "patients"), lets the
 * admin send a test message and browse the recent audit log. Template status +
 * sync moved to NiV → הודעות → תבניות (messages/TemplatesPanel.js, Phase 1).
 * Backed by the `nivWhatsAppAdmin` callable (nivAdmin only). Error codes: CK-10401 – CK-10404.
 */

import { useMemo, useState } from "react";
import {
  ArrowsClockwise,
  CheckCircle,
  Copy,
  PaperPlaneTilt,
  Warning,
  WhatsappLogo,
  XCircle,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import {
  useGetWhatsAppStatusQuery,
  useGetWhatsAppRecentMessagesQuery,
  useRunWhatsAppActionMutation,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";

const CHANNEL_LABELS = {
  users: "לקוחות / לידים (ניב)",
  patients: "מטופלים (קליניקה → מטופל)",
};

const MSG_STATUS_LABELS = {
  sent: "נשלח",
  delivered: "נמסר",
  read: "נקרא",
  failed: "נכשל",
  received: "התקבל",
};

const DIRECTION_LABELS = { outbound: "יוצא", inbound: "נכנס" };

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("he-IL", {
      timeZone: "Asia/Jerusalem",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function Tone({ tone, children }) {
  return <span className={`nwa-pill nwa-pill-${tone}`}>{children}</span>;
}

function NivWhatsAppTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const {
    data: status,
    isLoading: statusLoading,
    isFetching: statusFetching,
    refetch: refetchStatus,
  } = useGetWhatsAppStatusQuery(undefined, { skip: !isAuthorized });
  const [logChannel, setLogChannel] = useState("");
  const { data: messages = [], isFetching: logFetching } = useGetWhatsAppRecentMessagesQuery(
    { channel: logChannel || undefined, limit: 40 },
    { skip: !isAuthorized }
  );
  const [runAction, { isLoading: acting }] = useRunWhatsAppActionMutation();

  // Quick connectivity test — TEST_MESSAGE template to any number (CK-10404)
  const [quickPhone, setQuickPhone] = useState("");
  const [quickResult, setQuickResult] = useState(null);

  const [testChannel, setTestChannel] = useState("users");
  const [testPhone, setTestPhone] = useState("");
  const [testMode, setTestMode] = useState("template");
  const [testTemplate, setTestTemplate] = useState("LEAD_WELCOME");
  const [testName, setTestName] = useState("");
  const [testText, setTestText] = useState("");
  const [lastResult, setLastResult] = useState(null);

  const templates = status?.templates || [];
  const channels = status?.channels || {};
  const configured = status?.configured || {};
  const allConfigured = useMemo(() => {
    const cfg = status?.configured || {};
    return Object.values(cfg).every(Boolean) && !!status?.channels?.users?.configured;
  }, [status]);

  const canSendTest =
    !acting &&
    testPhone.trim().length >= 9 &&
    (testMode === "template" ? !!testTemplate : testText.trim().length > 0);

  const handleSendTest = async () => {
    if (!canSendTest) return;
    try {
      const payload = {
        channel: testChannel,
        phone: testPhone.trim(),
        ...(testMode === "template"
          ? { templateId: testTemplate, vars: { fullName: testName.trim() } }
          : { text: testText.trim() }),
      };
      const res = await runAction({ action: "sendTest", payload }).unwrap();
      setLastResult(res);
      if (res?.ok) showSuccess("הודעת הבדיקה נשלחה");
      else showError(res?.error?.metaMessage || "השליחה נכשלה");
    } catch (error) {
      console.error("[CK-10401] WhatsApp test send failed:", {
        errorCode: "CK-10401",
        errorMessage: error?.message,
        channel: testChannel,
        timestamp: new Date().toISOString(),
      });
      showError("שליחת הבדיקה נכשלה");
    }
  };

  const canQuickSend = !acting && quickPhone.replace(/\D/g, "").length >= 9;

  const handleQuickTest = async () => {
    if (!canQuickSend) return;
    try {
      const res = await runAction({
        action: "sendTest",
        payload: { channel: "users", phone: quickPhone.trim(), templateId: "TEST_MESSAGE" },
      }).unwrap();
      setQuickResult(res);
      if (res?.ok) showSuccess("הודעת הבדיקה נשלחה בוואטסאפ");
      else showError(res?.error?.metaMessage || "השליחה נכשלה");
    } catch (error) {
      console.error("[CK-10404] WhatsApp quick test failed:", {
        errorCode: "CK-10404",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("שליחת הבדיקה נכשלה");
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(status?.webhookUrl || "");
      showSuccess("כתובת ה-Webhook הועתקה");
    } catch (error) {
      console.error("[CK-10402] Copy webhook URL failed:", {
        errorCode: "CK-10402",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("ההעתקה נכשלה");
    }
  };

  return (
    <div className="niv-push-content nwa">
      <div className="niv-push-header">
        <h3>
          <WhatsappLogo size={22} weight="fill" />
          WhatsApp — Meta Cloud API
        </h3>
        <button
          type="button"
          className="niv-btn niv-btn-secondary niv-btn-sm"
          onClick={() => refetchStatus()}
          disabled={statusFetching}
        >
          <ArrowsClockwise size={14} className={statusFetching ? "spinning" : ""} /> רענון
        </button>
      </div>

      {/* ---- Quick test: one number, one button ---- */}
      <section className="nwa-section nwa-quick">
        <div className="nwa-section-head">
          <h4>בדיקת WhatsApp</h4>
          <span className="nwa-note">שולח את תבנית הבדיקה למספר שתזין</span>
        </div>
        <div className="nwa-quick-row">
          <input
            type="tel"
            dir="ltr"
            className="niv-push-input"
            placeholder="050-0000000"
            value={quickPhone}
            onChange={(e) => setQuickPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleQuickTest()}
            aria-label="מספר טלפון לבדיקה"
          />
          <button
            type="button"
            className="niv-btn niv-btn-primary"
            onClick={handleQuickTest}
            disabled={!canQuickSend}
          >
            {acting ? <LoaderLoop size="xs" /> : <WhatsappLogo size={16} weight="fill" />}
            שלח הודעת בדיקה
          </button>
        </div>
        {quickResult && (
          <div className={`niv-push-result ${quickResult.ok ? "" : "nwa-result-bad"}`}>
            {quickResult.ok ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
            <span dir="ltr">
              {quickResult.ok
                ? `sent · ${quickResult.messageId || ""}`
                : `${quickResult.error?.code || ""} · ${quickResult.error?.metaMessage || "failed"}`}
            </span>
          </div>
        )}
      </section>

      {statusLoading ? (
        <ContentLoader size="sm" compact message="טוען סטטוס…" />
      ) : (
        <>
          {/* ---- Config / channels ---- */}
          <section className="nwa-section">
            <div className="nwa-section-head">
              <h4>הגדרות וערוצים</h4>
              {allConfigured ? (
                <Tone tone="ok">
                  <CheckCircle size={12} weight="fill" /> מוגדר
                </Tone>
              ) : (
                <Tone tone="bad">
                  <Warning size={12} weight="fill" /> חסרים מפתחות ב-functions/.env
                </Tone>
              )}
            </div>
            <div className="nwa-config-grid">
              {[
                ["accessToken", "Access token"],
                ["wabaId", "WABA id"],
                ["verifyToken", "Verify token"],
                ["appSecret", "App secret"],
                ["smsConfigured", "SMS fallback (ActiveTrail)"],
              ].map(([key, label]) => (
                <div key={key} className="nwa-config-item">
                  <span>{label}</span>
                  {configured[key] ? (
                    <CheckCircle size={16} weight="fill" className="nwa-ok" />
                  ) : (
                    <XCircle size={16} weight="fill" className="nwa-bad" />
                  )}
                </div>
              ))}
            </div>
            <div className="nwa-channels">
              {Object.values(channels).map((ch) => (
                <div key={ch.channel} className="nwa-channel-card">
                  <div className="nwa-channel-head">
                    <strong>{CHANNEL_LABELS[ch.channel] || ch.channel}</strong>
                    {ch.configured ? <Tone tone="ok">פעיל</Tone> : <Tone tone="bad">לא מוגדר</Tone>}
                  </div>
                  <div className="nwa-channel-meta">
                    <span dir="ltr">{ch.displayPhoneNumber || ch.phoneNumberId || "—"}</span>
                    {ch.verifiedName && <span>{ch.verifiedName}</span>}
                    {ch.qualityRating && <span>איכות: {ch.qualityRating}</span>}
                    {ch.sharesUsersNumber && <Tone tone="ok">מספר אחד לשני הערוצים (לפי תכנון)</Tone>}
                    {ch.error && <span className="nwa-bad">{ch.error}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="nwa-webhook">
              <span className="niv-push-label">Webhook URL (Meta App → WhatsApp → Configuration)</span>
              <div className="nwa-webhook-row">
                <code dir="ltr">{status?.webhookUrl}</code>
                <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={copyWebhook}>
                  <Copy size={14} /> העתק
                </button>
              </div>
            </div>
          </section>

          {/* Templates: NiV → הודעות → תבניות */}
          <p className="nwa-note">
            תבניות Meta (סטטוס אישור, סנכרון) עברו ללשונית <strong>הודעות ← תבניות</strong>.
          </p>

          {/* ---- Test send ---- */}
          <section className="nwa-section">
            <div className="nwa-section-head">
              <h4>שליחת בדיקה</h4>
            </div>
            <div className="nwa-test-grid">
              <div className="niv-push-form-group">
                <label className="niv-push-label" htmlFor="nwa-channel">ערוץ</label>
                <select
                  id="nwa-channel"
                  className="niv-push-input"
                  value={testChannel}
                  onChange={(e) => setTestChannel(e.target.value)}
                >
                  <option value="users">{CHANNEL_LABELS.users}</option>
                  <option value="patients">{CHANNEL_LABELS.patients}</option>
                </select>
              </div>
              <div className="niv-push-form-group">
                <label className="niv-push-label" htmlFor="nwa-phone">טלפון</label>
                <input
                  id="nwa-phone"
                  type="tel"
                  dir="ltr"
                  className="niv-push-input"
                  placeholder="050-0000000"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
              </div>
              <div className="niv-push-form-group">
                <label className="niv-push-label" htmlFor="nwa-mode">סוג</label>
                <select
                  id="nwa-mode"
                  className="niv-push-input"
                  value={testMode}
                  onChange={(e) => setTestMode(e.target.value)}
                >
                  <option value="template">תבנית מאושרת</option>
                  <option value="text">טקסט חופשי (רק בתוך חלון 24 שעות)</option>
                </select>
              </div>
              {testMode === "template" ? (
                <>
                  <div className="niv-push-form-group">
                    <label className="niv-push-label" htmlFor="nwa-template">תבנית</label>
                    <select
                      id="nwa-template"
                      className="niv-push-input"
                      value={testTemplate}
                      onChange={(e) => setTestTemplate(e.target.value)}
                    >
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="niv-push-form-group">
                    <label className="niv-push-label" htmlFor="nwa-name">שם (למשתנה {"{{1}}"})</label>
                    <input
                      id="nwa-name"
                      type="text"
                      className="niv-push-input"
                      placeholder="ישראל"
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                    />
                  </div>
                </>
              ) : (
                <div className="niv-push-form-group nwa-span-2">
                  <label className="niv-push-label" htmlFor="nwa-text">תוכן</label>
                  <textarea
                    id="nwa-text"
                    className="niv-push-textarea"
                    rows={3}
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                    maxLength={1000}
                  />
                </div>
              )}
            </div>
            <button
              type="button"
              className="niv-btn niv-btn-primary niv-push-send-btn"
              onClick={handleSendTest}
              disabled={!canSendTest}
            >
              {acting ? (
                <>
                  <LoaderLoop size="xs" /> שולח…
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={18} weight="fill" /> שלח בדיקה
                </>
              )}
            </button>
            {lastResult && (
              <div className={`niv-push-result ${lastResult.ok ? "" : "nwa-result-bad"}`}>
                {lastResult.ok ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                <span dir="ltr">
                  {lastResult.ok
                    ? `sent · ${lastResult.messageId || ""}`
                    : `${lastResult.error?.code || ""} · ${lastResult.error?.metaMessage || "failed"}`}
                </span>
              </div>
            )}
          </section>

          {/* ---- Recent log ---- */}
          <section className="nwa-section">
            <div className="nwa-section-head">
              <h4>הודעות אחרונות</h4>
              <select
                className="niv-push-input nwa-log-filter"
                value={logChannel}
                onChange={(e) => setLogChannel(e.target.value)}
                aria-label="סינון ערוץ"
              >
                <option value="">כל הערוצים</option>
                <option value="users">{CHANNEL_LABELS.users}</option>
                <option value="patients">{CHANNEL_LABELS.patients}</option>
              </select>
            </div>
            {logFetching && messages.length === 0 ? (
              <ContentLoader size="sm" compact message="טוען…" />
            ) : messages.length === 0 ? (
              <p className="nwa-note">אין הודעות עדיין.</p>
            ) : (
              <div className="niv-info-table-scroll">
                <table className="nwa-table">
                  <thead>
                    <tr>
                      <th>מתי</th>
                      <th>ערוץ</th>
                      <th>כיוון</th>
                      <th>אל / מאת</th>
                      <th>תוכן</th>
                      <th>סטטוס</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.id}>
                        <td>{formatWhen(m.createdAt)}</td>
                        <td>{m.channel === "users" ? "לקוחות" : "מטופלים"}</td>
                        <td>{DIRECTION_LABELS[m.direction] || m.direction}</td>
                        <td dir="ltr">
                          {m.contactName ? `${m.contactName} · ` : ""}
                          {m.to || m.from || ""}
                        </td>
                        <td className="nwa-preview" title={m.preview || ""}>
                          {m.templateId ? `[${m.templateId}] ` : ""}
                          {m.preview || ""}
                        </td>
                        <td>
                          <Tone tone={m.status === "failed" ? "bad" : m.status === "read" || m.status === "delivered" ? "ok" : "warn"}>
                            {MSG_STATUS_LABELS[m.status] || m.status}
                          </Tone>
                          {m.error && <div className="nwa-bad nwa-err">{m.error}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default NivWhatsAppTool;
